import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";

const scroller = () => document.querySelector<HTMLElement>(".pn-app > .pn-main");

/**
 * A screen pushed inside a page through its address (`?c=meta`, `?view=plan`): it opens at its top, and
 * going back puts the page where it was left. Why: the shell resets the scroll only when the path changes.
 */
export function useSubScroll(sub: string | null): void {
  const pageTop = useRef(0);
  const shown = useRef(sub);

  useEffect(() => {
    const el = scroller();
    if (!el) return;
    const keep = () => {
      if (shown.current === null) pageTop.current = el.scrollTop;
    };
    el.addEventListener("scroll", keep, { passive: true });
    return () => el.removeEventListener("scroll", keep);
  }, []);

  useLayoutEffect(() => {
    if (shown.current === sub) return;
    shown.current = sub;
    const el = scroller();
    if (el) el.scrollTop = sub === null ? pageTop.current : 0;
  }, [sub]);
}

/**
 * True once the screen holding `ref` has scrolled past `px`: a screen with its own big heading shows its
 * name in the bar only then, as the phone's own screens do.
 */
export function useScrolledPast(ref: RefObject<HTMLElement | null>, px: number): boolean {
  const [past, setPast] = useState(false);
  useEffect(() => {
    const el = ref.current?.closest<HTMLElement>(".pn-pane, .pn-main");
    if (!el) return;
    const read = () => setPast(el.scrollTop > px);
    read();
    el.addEventListener("scroll", read, { passive: true });
    return () => el.removeEventListener("scroll", read);
  }, [ref, px]);
  return past;
}

/** Puts the pane holding `ref` back at its top whenever `key` changes: a new pick in a split opens at its start. */
export function usePaneTop(ref: RefObject<HTMLElement | null>, key: string): void {
  useLayoutEffect(() => {
    ref.current?.closest<HTMLElement>(".pn-pane")?.scrollTo({ top: 0 });
  }, [ref, key]);
}
